import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    CircularProgress,
    alpha,
    Alert,
} from '@mui/material';
import axios from 'axios';
import { validateCertificationForm } from './certification.validation';

const RSATVA_OPTIONS = [
    { value: 'RSATVA0', label: 'Sans RS au titre de TVA (0%)' },
    { value: 'RSATVA25', label: 'RS au titre de TVA (25%)' },
];

const CertificationInsertForm = ({
    clientId,
    beneficiary,
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    onBack,
    onNext,
    palette,
}) => {
    const [nomenclatureLoading, setNomenclatureLoading] = useState(false);
    const [operationTypeOptions, setOperationTypeOptions] = useState([]);
    const [operationOptions, setOperationOptions] = useState([]);
    const [combinationLoading, setCombinationLoading] = useState(false);
    const [combinationData, setCombinationData] = useState(null);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleNumericChange = (field, value, maxLength) => {
        const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
        handleChange(field, numericValue);
    };

    // Charger les types d'operation
    useEffect(() => {
        const fetchOperationNomenclature = async () => {
            if (!clientId) return;
            try {
                setNomenclatureLoading(true);
                const response = await axios.get(
                    `/api/certif/operation-nomenclature/${clientId}`,
                    {
                        params: {
                            resident: beneficiary?.resident ?? true,
                            paymentDate: formData.paymentDate,
                        },
                    }
                );
                const data = response.data?.data || response.data || [];
                setOperationTypeOptions(Array.isArray(data) ? data : []);
            } catch {
                setOperationTypeOptions([]);
            } finally {
                setNomenclatureLoading(false);
            }
        };
        fetchOperationNomenclature();
    }, [clientId, beneficiary?.resident, formData.paymentDate]);

    // Charger les operations selon le type selectionne
    useEffect(() => {
        const fetchOperationsByType = async () => {
            if (!clientId || !formData.operationType) {
                setOperationOptions([]);
                return;
            }
            try {
                setNomenclatureLoading(true);
                const response = await axios.get(
                    `/api/certif/operation-details/${clientId}`,
                    {
                        params: {
                            resident: beneficiary?.resident ?? true,
                            operationTypeId: formData.operationType,
                            paymentDate: formData.paymentDate,
                        },
                    }
                );
                const data = response.data?.data || response.data || [];
                setOperationOptions(Array.isArray(data) ? data : []);
            } catch {
                setOperationOptions([]);
            } finally {
                setNomenclatureLoading(false);
            }
        };
        fetchOperationsByType();
    }, [clientId, beneficiary?.resident, formData.operationType, formData.paymentDate]);

    // Appeler /combination/get quand on a operation + prise en charge + convention
    const fetchCombination = useCallback(async () => {
        const operationCode = formData.operationCode;
        if (!clientId || !operationCode || !formData.isSupported || !formData.hasConvention) {
            return;
        }

        setCombinationLoading(true);
        try {
            const response = await axios.post(`/api/certif/combination/${clientId}`, {
                idTypeOperation: operationCode,
                cnpc: formData.hasConvention === 'oui',
                pcharge: formData.isSupported === 'oui',
                resident: beneficiary?.resident ?? true,
                paymentDate: formData.paymentDate || '',
            });

            const data = response.data?.data;
            setCombinationData(data);

            // Mettre a jour le taux RS avec la valeur reelle de TEJ
            if (data?.value != null) {
                setFormData((prev) => ({ ...prev, tauxRS: data.value }));
            }
        } catch (err) {
            console.error('Erreur combination:', err);
            setCombinationData(null);
        } finally {
            setCombinationLoading(false);
        }
    }, [clientId, formData.operationCode, formData.isSupported, formData.hasConvention, formData.paymentDate, beneficiary?.resident, setFormData]);

    useEffect(() => {
        fetchCombination();
    }, [fetchCombination]);

    // Quand on selectionne une operation, stocker son code
    const handleOperationSelect = (operationId) => {
        const selectedOp = operationOptions.find(
            (op) => (op.id || op.value) === operationId || op.code === operationId
        );

        setFormData((prev) => ({
            ...prev,
            operation: operationId,
            operationCode: selectedOp?.code || '',
            operationLabel: selectedOp?.label || selectedOp?.designation || '',
            tauxRS: '', // sera rempli par /combination/get
        }));
        setCombinationData(null);
        setFormErrors((prev) => ({ ...prev, operation: '' }));
    };

    const handleSubmit = () => {
        const errors = validateCertificationForm(formData);
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;
        onNext();
    };

    const isLoading = nomenclatureLoading || combinationLoading;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                border: `1px solid ${alpha(palette.navy, 0.08)}`,
                backgroundColor: alpha('#ffffff', 0.9),
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 700, color: palette.textPrimary, mb: 3 }}>
                Informations du certificat
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        required
                        type="date"
                        label="Date de paiement"
                        value={formData.paymentDate}
                        onChange={(e) => handleChange('paymentDate', e.target.value)}
                        error={!!formErrors.paymentDate}
                        helperText={formErrors.paymentDate}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        required
                        label="Numero chez le declarant"
                        value={formData.declarantNumber}
                        onChange={(e) => handleNumericChange('declarantNumber', e.target.value, 15)}
                        error={!!formErrors.declarantNumber}
                        helperText={formErrors.declarantNumber}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(palette.navy, 0.1)}` }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Exercice de facturation"
                                    value={formData.billingExercise}
                                    onChange={(e) => handleNumericChange('billingExercise', e.target.value, 4)}
                                    error={!!formErrors.billingExercise}
                                    helperText={formErrors.billingExercise}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth required error={!!formErrors.operationType}>
                                    <InputLabel id="operation-type-label">Type operation</InputLabel>
                                    <Select
                                        labelId="operation-type-label"
                                        label="Type operation"
                                        value={formData.operationType}
                                        onChange={(e) => {
                                            handleChange('operationType', e.target.value);
                                            handleChange('operation', '');
                                            handleChange('operationCode', '');
                                            handleChange('operationLabel', '');
                                            handleChange('tauxRS', '');
                                            handleChange('rsaTva', '');
                                            handleChange('tauxRsaTva', '');
                                            setOperationOptions([]);
                                            setCombinationData(null);
                                        }}
                                        disabled={nomenclatureLoading}
                                    >
                                        {operationTypeOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {!!formErrors.operationType && (
                                        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.75, ml: 1.75 }}>
                                            {formErrors.operationType}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth required error={!!formErrors.operation}>
                                    <InputLabel id="operation-label">Operation</InputLabel>
                                    <Select
                                        labelId="operation-label"
                                        label="Operation"
                                        value={formData.operation}
                                        onChange={(e) => handleOperationSelect(e.target.value)}
                                        disabled={!formData.operationType || nomenclatureLoading}
                                    >
                                        {operationOptions.map((option) => (
                                            <MenuItem key={option.code || option.id} value={option.code || option.id}>
                                                {option.label || option.designation}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {!!formErrors.operation && (
                                        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.75, ml: 1.75 }}>
                                            {formErrors.operation}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: palette.textPrimary, mb: 1 }}>
                                    Prise en charge *
                                </Typography>
                                <RadioGroup row value={formData.isSupported} onChange={(e) => handleChange('isSupported', e.target.value)}>
                                    <FormControlLabel value="oui" control={<Radio />} label="Oui" />
                                    <FormControlLabel value="non" control={<Radio />} label="Non" />
                                </RadioGroup>
                                {!!formErrors.isSupported && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f' }}>{formErrors.isSupported}</Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: palette.textPrimary, mb: 1 }}>
                                    Convention *
                                </Typography>
                                <RadioGroup row value={formData.hasConvention} onChange={(e) => handleChange('hasConvention', e.target.value)}>
                                    <FormControlLabel value="oui" control={<Radio />} label="Oui" />
                                    <FormControlLabel value="non" control={<Radio />} label="Non" />
                                </RadioGroup>
                                {!!formErrors.hasConvention && (
                                    <Typography variant="caption" sx={{ color: '#d32f2f' }}>{formErrors.hasConvention}</Typography>
                                )}
                            </Grid>

                            {/* Taux RS depuis /combination/get */}
                            {combinationLoading && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                                            Chargement du taux RS...
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}

                            {combinationData && !combinationLoading && (
                                <>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Taux RS"
                                            value={`${combinationData.value}%`}
                                            InputProps={{ readOnly: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: alpha(palette.teal, 0.06),
                                                },
                                            }}
                                            helperText={
                                                combinationData.writable
                                                    ? 'Taux modifiable dans les montants'
                                                    : 'Taux fixe'
                                            }
                                        />
                                    </Grid>

                                    {/* RS au titre de TVA */}
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <InputLabel id="rsatva-label">RS au titre de TVA</InputLabel>
                                            <Select
                                                labelId="rsatva-label"
                                                label="RS au titre de TVA"
                                                value={formData.rsaTva || 'RSATVA0'}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleChange('rsaTva', val);
                                                    if (val === 'RSATVA25') {
                                                        handleChange('tauxRsaTva', '25');
                                                    } else {
                                                        handleChange('tauxRsaTva', '');
                                                    }
                                                }}
                                            >
                                                {RSATVA_OPTIONS.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Taux RS au titre de TVA (si RSATVA25) */}
                                    {formData.rsaTva === 'RSATVA25' && (
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Taux RS au titre de TVA"
                                                value={`${formData.tauxRsaTva || 25}%`}
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(palette.navy, 0.04),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}

                            {!combinationData && formData.tauxRS !== '' && formData.tauxRS !== undefined && !combinationLoading && (
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Taux RS (auto)"
                                        value={`${formData.tauxRS}%`}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: alpha(palette.teal, 0.06),
                                            },
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={onBack}
                                        sx={{ minWidth: 120, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Retour
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        sx={{
                                            px: 3, py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: 'none',
                                            background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                            boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                                            '&:hover': { background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)` },
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Suivant'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CertificationInsertForm;
